import { useEffect, useState } from 'react';
import {
  fetchHasPublishedNews,
  getPublishedPost,
  listPublishedPosts,
  readHasNewsHint,
  readNewsSnapshot,
} from '../../utils/posts';

/**
 * True once at least one post is published. Drives the News links in the
 * header and footer. Starts from the build's answer so prerendered pages
 * hydrate with the same links they were rendered with.
 */
export function useHasPublishedNews() {
  const [hasNews, setHasNews] = useState(readHasNewsHint);

  useEffect(() => {
    let cancelled = false;
    fetchHasPublishedNews().then((value) => {
      if (!cancelled && value !== null) setHasNews(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return hasNews;
}

// The prerendered snapshot (if this exact path was built with one) seeds the
// first render so hydration matches the static HTML; Supabase then refreshes it
// after mount so a post published since the last deploy still shows up.

export function usePublishedPosts(path, { limit } = {}) {
  const snapshot = readNewsSnapshot(path);
  const [posts, setPosts] = useState(() => snapshot?.posts?.slice(0, limit) ?? []);
  const [loading, setLoading] = useState(!snapshot);

  useEffect(() => {
    let cancelled = false;
    listPublishedPosts({ limit }).then(({ posts: fresh, error }) => {
      if (cancelled) return;
      // A failed refresh (offline, table not deployed yet) keeps whatever the
      // snapshot showed rather than blanking the section.
      if (!error) setPosts(fresh);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [limit]);

  return { posts, loading };
}

export function usePublishedPost(slug) {
  const path = `/news/${slug}`;
  const snapshot = readNewsSnapshot(path);
  const seeded = snapshot?.post?.slug === slug ? snapshot.post : null;
  const [post, setPost] = useState(seeded);
  const [loading, setLoading] = useState(!seeded);

  useEffect(() => {
    let cancelled = false;
    if (!seeded) {
      setPost(null);
      setLoading(true);
    }
    getPublishedPost(slug).then(({ post: fresh, error }) => {
      if (cancelled) return;
      if (!error) setPost(fresh);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // `seeded` is derived from slug; re-running on its identity would refetch
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return { post, loading };
}
